import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { FileText, Upload, Loader2, Trash2, Download, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { projectFilesApi } from '../api';
import { fmtDate } from '../constants';
import { fmtBytes } from '../expenseConstants';
import { downloadProjectFile } from './ProjectPhotosTab';

export const ProjectDocumentsTab = ({ project }) => {
  const fileRef = useRef(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const locked = Boolean(project.is_archived);

  const load = useCallback(() => {
    projectFilesApi.list({ project_id: project.id, kind: 'document' })
      .then((r) => setDocs(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('project_id', project.id);
      fd.append('kind', 'document');
      try { await projectFilesApi.upload(fd); ok += 1; } catch { fail += 1; }
    }
    setUploading(false);
    if (ok) toast.success(`${ok} document${ok === 1 ? '' : 's'} uploaded`);
    if (fail) toast.error(`${fail} failed (PDF or image up to 15 MB)`);
    load();
  };

  const confirmDelete = async () => {
    try {
      await projectFilesApi.delete(deleting.id);
      toast.success('Document deleted');
      setDeleting(null);
      load();
    } catch { toast.error('Could not delete document'); }
  };

  return (
    <div data-testid="project-documents-tab">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white">Project Documents</h3>
          <p className="text-sm text-gray-500">Contracts, permits, plans, and other paperwork for this project.</p>
        </div>
        {!locked && (
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="documents-upload-btn">
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload Documents
          </Button>
        )}
        <input ref={fileRef} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" multiple onChange={handleFiles} className="hidden" data-testid="documents-file-input" />
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-500" data-testid="documents-loading">
          <Loader2 size={24} className="mx-auto mb-2 animate-spin text-[#D4AF37]" /> Loading documents…
        </div>
      ) : docs.length === 0 ? (
        <Card className="bg-[#111111] border-gray-800 p-12 text-center" data-testid="documents-empty">
          <FileText size={32} className="mx-auto mb-3 text-gray-700" />
          <p className="text-white font-medium mb-1">No documents yet</p>
          <p className="text-gray-500 text-sm">Upload PDFs or scans to keep this project's paperwork in one place.</p>
        </Card>
      ) : (
        <Card className="bg-[#111111] border-gray-800 overflow-hidden">
          <div className="divide-y divide-gray-800/70" data-testid="documents-list">
            {docs.map((d) => (
              <div key={d.id} className="flex items-center gap-3 px-4 py-3" data-testid={`document-row-${d.id}`}>
                <div className="h-9 w-9 rounded-md bg-[#1F1F1F] flex items-center justify-center shrink-0">
                  {d.mime_type === 'application/pdf'
                    ? <FileText size={17} className="text-[#D4AF37]" />
                    : <ImageIcon size={17} className="text-[#D4AF37]" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">{d.caption || d.original_filename}</div>
                  <div className="text-[11px] text-gray-500">
                    {fmtBytes(d.size_bytes)} · {fmtDate(d.created_at?.slice(0, 10))} · {d.created_by_username}
                  </div>
                </div>
                <button onClick={() => downloadProjectFile(d)} className="text-gray-500 hover:text-[#D4AF37] p-1.5" title="Download" data-testid={`document-download-${d.id}`}>
                  <Download size={16} />
                </button>
                {!locked && (
                  <button onClick={() => setDeleting(d)} className="text-gray-500 hover:text-red-400 p-1.5" title="Delete" data-testid={`document-delete-${d.id}`}>
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      <AlertDialog open={Boolean(deleting)} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="bg-[#111111] border-gray-800 text-white" data-testid="document-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this document?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              "{deleting?.caption || deleting?.original_filename}" will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-gray-700 text-gray-300 hover:bg-[#1A1A1A]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 text-white hover:bg-red-700" data-testid="document-delete-confirm-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
